import { useState } from 'react';

import { selectCurrentUser } from 'features/auth/slice';
import { useSelector } from 'react-redux';
import NextLink from 'next/link';
import { trackEvent } from 'fathom-client';

import {
  Button,
  flash,
  FieldWrapper,
  Icon,
  Label,
  LinkWithButtonStyles,
  TextInput,
} from '@frontendmentorio/design-system-v2';

export default function Template() {
  const currentUser = useSelector(selectCurrentUser);

  const [managerEmail, setManagerEmail] = useState('');
  const [managerName, setManagerName] = useState('');
  const [userName, setUserName] = useState('');

  const body = `Hi ${managerName},\n\nI'd like to use my training budget for a Frontend Mentor Pro seat. Their real-world projects will help me gain experience with our tech stack.\n\nCould you please create an account on Frontend Mentor for Teams (${process.env.NEXT_PUBLIC_TEAMS_PLATFORM_URL}) and add me? My username is ${currentUser.username}.\n\nThe annual cost is within my allowance and should translate into improved skills and increased productivity as I refine my workflow.\n\nThanks for considering my request.\n\nBest,\n\n${userName}`;

  const href = encodeURI(
    `mailto:${managerEmail}?subject=Frontend Mentor for Teams subscription request&body=${body}`
  );

  const handleCopy = () => {
    navigator.clipboard.writeText(body);
    flash.success('Template copied!');
    trackEvent('Get Pro for Free: template copied');
  };

  return (
    <div className="flex flex-col gap-5 items-center text-start">
      <div className="bg-gray-100 border border-neutral-200 flex flex-col gap-4 p-5 rounded-2xl w-full">
        <div className="flex gap-2 items-center">
          <span>To:</span>

          <FieldWrapper className="w-full">
            <Label className="sr-only">Manager's email</Label>

            <TextInput
              className="bg-neutral-50"
              placeholder="Manager's email"
              name="managerEmail"
              onChange={setManagerEmail}
            />
          </FieldWrapper>
        </div>

        <div className="flex gap-2 items-center">
          <span>Hi</span>

          <div className="flex gap-1 items-center">
            <FieldWrapper className="max-w-[10rem] w-full">
              <Label className="sr-only">Manager's name</Label>

              <TextInput
                className="bg-neutral-50"
                placeholder="Manager's name"
                name="managerName"
                onChange={setManagerName}
              />
            </FieldWrapper>

            <span>,</span>
          </div>
        </div>

        <p>
          I'd like to use my training budget for a Frontend Mentor Pro seat.
          Their real-world projects will help me gain experience with our tech
          stack.
        </p>

        <p>
          Could you please create an account on{' '}
          <NextLink
            className="underline hover:no-underline"
            href={process.env.NEXT_PUBLIC_TEAMS_PLATFORM_URL}
          >
            Frontend Mentor for Teams
          </NextLink>{' '}
          and add me? My username is{' '}
          <span className="font-semibold">{currentUser.username}</span>.
        </p>

        <p>
          The annual cost is within my allowance and should translate into
          improved skills and increased productivity as I refine my workflow.
        </p>

        <p>Thanks for considering my request.</p>

        <p>Best,</p>

        <FieldWrapper className="max-w-[10rem] w-full">
          <Label className="sr-only">Your name</Label>

          <TextInput
            className="bg-neutral-50"
            placeholder="Your name"
            name="userName"
            onChange={setUserName}
          />
        </FieldWrapper>
      </div>

      <div className="flex flex-col gap-4 items-center">
        <LinkWithButtonStyles
          href={href}
          onClick={() => trackEvent('Get Pro for Free: email client opened')}
        >
          Send email (opens your email client)
        </LinkWithButtonStyles>

        <Button
          className="border border-neutral-200 gap-2"
          level="tertiary"
          onClick={handleCopy}
        >
          <Icon className="text-lg" name="copy" />
          <span>Copy to clipboard</span>
        </Button>
      </div>
    </div>
  );
}
